import {
  type UseQueryResult,
  useQueries,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import type { AxiosError } from "axios";
import { useContext, useEffect } from "react";
import { configStore } from "../../../../contexts/ConfigContext";
import { dataStore } from "../../../../contexts/DataContext";
import { sessionStore } from "../../../../contexts/SessionContext";
import { useActiveSubject } from "../../../../hooks/useActiveSubject";
import { useUserId } from "../../../../hooks/useUserInfo";
import { isAxiosErrorStatus } from "../../../../utils/api-client-utils";
import { getPlaylistSubjects } from "../../../../utils/dataRetrieval";
import type {
  Assignment,
  Playlist,
  PlaylistItem,
  PlaylistProgress,
} from "../type";
import queryKeys from "./queryKeys";
import usePlaylistAPIClient from "./usePlaylistAPIClient";
import { usePlaylistableCollections } from "./usePlaylistableCollections";

export const usePlaylistQuery = (
  playlistId: string,
  options?: { enabled?: boolean },
) => {
  const {
    session: { userId },
  } = useContext(sessionStore);
  const { getPlaylist } = usePlaylistAPIClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.getPlaylist(playlistId, { author_id: userId }),
    queryFn: getPlaylist,
    initialData: queryClient
      .getQueryData<Playlist<PlaylistItem>[]>(
        queryKeys.getPlaylists({ author_id: userId }),
      )
      ?.find((playlist) => playlist.learning_set_id === playlistId),
    staleTime: Infinity,
    retry: (failureCount: number, error: AxiosError) =>
      failureCount < 3 && !isAxiosErrorStatus(error, 404),
    ...options,
  });
};

export const useAllPlaylistsQuery = () => {
  const author_id = useUserId();
  const { getPlaylists } = usePlaylistAPIClient();

  return useQuery({
    queryKey: queryKeys.getPlaylists({ author_id }),
    queryFn: getPlaylists,
    staleTime: Infinity,
  });
};

export const useFilteredPlaylists = (userId: string) => {
  const subject = useActiveSubject();

  const { data } = useContext(dataStore);
  const { data: collections } = usePlaylistableCollections();
  const {
    config: { mappingTree },
  } = useContext(configStore);
  const playlistsQuery = useAllPlaylistsQuery();
  return {
    data: playlistsQuery.data?.filter(
      (playlist) =>
        playlist.author_id === userId ||
        getPlaylistSubjects(
          playlist,
          { data, collections },
          mappingTree,
        ).includes(subject),
    ),
    query: playlistsQuery,
  };
};

export const usePlaylistAssignmentsQuery = (
  playlistId: string,
  options?: { enabled?: boolean },
) => {
  const teacher = useUserId();
  const { getAssignments } = usePlaylistAPIClient();
  const queryClient = useQueryClient();

  return useQuery({
    queryKey: queryKeys.getAssignments({ resource: playlistId }),
    queryFn: getAssignments,
    staleTime: Infinity,
    initialData: () =>
      queryClient
        .getQueryData<Assignment[]>(queryKeys.getAssignments({ teacher }))
        ?.filter(
          (assignment) =>
            assignment.assigned_resource?.resource_id === playlistId,
        ),
    ...options,
  });
};

const ignoredList: string[] = [];

export const usePlaylistsQueries = (
  playlists: { author_id: string; id: string }[] | undefined,
  options?: { enabled?: boolean },
) => {
  const { getPlaylist } = usePlaylistAPIClient();
  const filteredPlaylists =
    playlists?.filter((p) => !ignoredList.includes(p.id)) ?? [];

  const results = useQueries({
    queries: filteredPlaylists.map(({ id, author_id }) => ({
      queryKey: queryKeys.getPlaylist(id, {
        author_id: author_id,
      }),
      queryFn: getPlaylist,
      staleTime: 3 * 60 * 1000,
      retry: (failureCount: number, error: AxiosError) => {
        return failureCount < 3 && !isAxiosErrorStatus(error, 404);
      },
      ...options,
    })),
  });

  useEffect(() => {
    /**
     * If an error occured we add the playlist id in the ignored list
     * to avoid an infinite retry loop
     */
    results.forEach((result, index) => {
      const playlist = filteredPlaylists[index];
      if (
        playlist &&
        result.error &&
        !ignoredList.includes(playlist.id) &&
        isAxiosErrorStatus(result.error as AxiosError, 404)
      ) {
        ignoredList.push(playlist.id);
      }
    });
  }, [results]);

  return results;
};

export const useProgressQuery = () => {
  const userId = useUserId();
  const { getPlaylistProgress } = usePlaylistAPIClient();
  return useQuery({
    queryKey: queryKeys.getPlaylistProgress({ teacher_id: userId }),
    queryFn: getPlaylistProgress,
    staleTime: 2 * 60 * 10000,
  });
};

export const usePlaylistProgressQuery = (playlistId: string) => {
  const { data, promise, ...progressQuery } = useProgressQuery();
  const filtered: Omit<
    UseQueryResult<PlaylistProgress | undefined>,
    "refetch" | "promise"
  > = {
    ...progressQuery,
    data: data?.find((playlist) => playlist.id === playlistId),
  };
  return filtered;
};
